import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {DesignSelector} from '@Components/design-selector';
import {SocialMediaWizardDesignSelectorPreview} from '@Components/social-media/components/social-media-wizard-design-selector-preview';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import type {PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {getDesignGalleryHeightForSelectDesignStep, getFilteredDesigns} from '@Libraries/social-media-wizard-design-library';
import {getStartingURIForGallery} from '@Libraries/social-media-wizard-step-library';
import {getPostersPageIds} from '@Libraries/poster-library';
import {GA4EventName} from '@Libraries/ga-events';
import {
  disableNextButton,
  enableNextButton,
  updateDesignsToPublishAndTriggerNext,
  updateDesignSelectorTab,
  updateIsTemplateSelected,
  updateTriggerNext,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {noop} from '@Utils/general.util';
import {useAppDispatch} from '@/hooks';
import type {WizardDesignSelectorPanelProps} from './wizard-design-selector-panel.types';
import {MAX_UPLOADS_TO_DESIGNS} from './wizard-design-selector-panel.types';
import styles from './wizard-design-selector-panel.module.scss';

export function WizardDesignSelectorPanel({
  designsToPublish,
  onDesignListChange,
  onDesignsStaged,
  designSelectorTab,
  isMobileView,
}: WizardDesignSelectorPanelProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const designSelectorRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [bottomShadowSelector, setBottomShadowSelector] = useState(false);
  const [isDesignSelectorLoading, setDesignSelectorIsLoading] = useState(false);
  const onUploadMediaDesignsCallbackRef = useRef<(designs: DesignSelection[]) => void>(noop);

  useEffect((): VoidFunction => {
    const previewContainerEl = previewContainerRef.current;
    const designSelectorEl = designSelectorRef.current;

    if (!previewContainerEl || !designSelectorEl) {
      return noop;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === previewContainerEl && entry.isIntersecting) {
          setBottomShadowSelector(false);
        } else {
          setBottomShadowSelector(true);
        }
      });
    });

    observer.observe(previewContainerEl);
    observer.observe(designSelectorEl);

    return () => {
      observer.disconnect();
    };
    // Re-attach on tab change: the preview container only mounts outside the GALLERY_TEMPLATES tab.
  }, [designSelectorTab]);

  onUploadMediaDesignsCallbackRef.current = (designs: DesignSelection[]): void => {
    const mergedDesigns = [...designsToPublish, ...getFilteredDesigns(designs, MAX_UPLOADS_TO_DESIGNS)];
    onDesignsStaged(mergedDesigns);
    setDesignSelectorIsLoading(false);
    updateTriggerDelayedNext();
  };

  const updateTriggerDelayedNext = (): void => {
    setTimeout((): void => {
      dispatch(updateTriggerNext());
    }, 100);
  };

  const onTabSelected = (newTab: DesignSelectorTab): void => {
    if (newTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      onDesignsStaged([]);
    }

    dispatch(updateDesignSelectorTab(newTab));
  };

  const handleDesignListChange = (designs: DesignSelection[]): void => {
    if (designs.length === 0) {
      dispatch(disableNextButton());
    } else {
      dispatch(enableNextButton());
    }

    onDesignListChange(designs);
  };

  const handleGalleryTemplatesSelected = (templateDesigns: DesignSelection[]): void => {
    dispatch(updateIsTemplateSelected(true));
    onDesignsStaged(templateDesigns);
    dispatch(updateDesignsToPublishAndTriggerNext(templateDesigns));
  };

  const getStartDesignFromUploadBtnOpts = (): CreateDesignFromMediaModalOpts => {
    return {
      allowMultipagePosterCreation: false,
      maxSelectionNumber: MAX_UPLOADS_TO_DESIGNS,
      shouldUseFastPendingImageUploadFlow: false,
      onAllPostersCreated: (posterHashedIds: PosterHashedId[]): void => {
        getPostersPageIds(posterHashedIds)
          .then((pageIdsMap: Record<PosterHashedId, PageHashedId[]>) => {
            const designSelections = Object.keys(pageIdsMap).flatMap((posterId) => {
              return pageIdsMap[posterId].map((pageId): DesignSelection => {
                return {posterHashedId: posterId, pageHashedId: pageId};
              });
            });

            onUploadMediaDesignsCallbackRef.current(designSelections);
          })
          .catch((err: unknown) => {
            console.error('Error fetching poster page IDs:', err);
          });
      },
      onPosterCreated: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CREATED);
      },
      onTriggered: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CLICKED);
      },
      onPosterCreationsStarted: (): void => {
        setDesignSelectorIsLoading(true);
        dispatch(disableNextButton());
      },
    };
  };

  const getDesignSelectorPreviewClasses = (): string => {
    const mobileViewClasses = isMobileView ? styles.carouselClasses : ``;

    if (bottomShadowSelector) {
      return `${mobileViewClasses} ${styles.bottomShadow}`;
    }

    return mobileViewClasses;
  };

  const shouldShowDesignSelectorPreview = (): boolean => {
    return designsToPublish.length !== 0 && designSelectorTab !== DesignSelectorTab.GALLERY_TEMPLATES;
  };

  const getDesignSelectorPreview = (): ReactElement<any> | null => {
    if (!shouldShowDesignSelectorPreview()) {
      return null;
    }

    return (
      <SocialMediaWizardDesignSelectorPreview
        containerClasses={getDesignSelectorPreviewClasses()}
        onUpdateDesignsToPublishTo={handleDesignListChange}
        designsToPublishTo={designsToPublish}
      />
    );
  };

  const getSelectorContainerIfNeeded = (): ReactElement<any> | null => {
    if (designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      return null;
    }

    return <div ref={previewContainerRef} className={`${styles.selectorContainer} ${isMobileView ? styles.mobileView : ''}`} />;
  };

  return (
    <div>
      <DesignSelector
        containerRef={designSelectorRef}
        initialDesignSelections={designsToPublish}
        onGalleryTemplateSelectionCallback={handleGalleryTemplatesSelected}
        onDesignsSelection={handleDesignListChange}
        designSelectorTab={designSelectorTab}
        onTabSelected={onTabSelected}
        startDesignFromMediaModalBtnOpts={getStartDesignFromUploadBtnOpts()}
        isLoading={isDesignSelectorLoading}
        designGalleryStartingURI={getStartingURIForGallery()}
        designGalleryHeight={getDesignGalleryHeightForSelectDesignStep()}
        scrollContainerEl={window}
      />
      {getDesignSelectorPreview()}
      {getSelectorContainerIfNeeded()}
    </div>
  );
}
